/**
 * World state — a plain, serializable object.
 *
 * "Plain and serializable" is a hard rule, not a style note: world state gets
 * hashed for determinism tests, snapshotted to SQLite (M0-07), and diffed for
 * the wire (M0-04). No class instances, no `Map`/`Set`, no functions, no
 * `undefined`, no cycles. If a system needs richer structure, it builds it
 * from state at the start of a tick and throws it away at the end.
 *
 * Later milestones widen `WorldState` in place: M0-04 adds actors and the map,
 * M3 adds weather and sites. Nothing else has to change when they do —
 * `cloneWorld` walks the object structurally rather than field by field, so a
 * new field cannot be forgotten into a shared reference.
 */
import { createTileMap, isPassable, setTile, tileAt } from './map.js';
import { createWeather } from './weather.js';
/** A fresh world at tick 0. No streams exist until something draws from one. */
export function createWorld(options) {
    const tick = options.tick ?? 0;
    if (!Number.isInteger(tick) || tick < 0) {
        throw new RangeError(`createWorld needs a non-negative integer tick (got ${tick})`);
    }
    if (options.seed === '') {
        throw new RangeError('createWorld needs a non-empty seed');
    }
    const dens = {};
    for (const den of options.dens ?? [])
        dens[den.id] = { ...den, living: [...den.living] };
    const flora = {};
    for (const [key, patch] of Object.entries(options.flora ?? {}))
        flora[key] = { ...patch };
    return {
        seed: options.seed,
        tick,
        rng: {},
        map: options.map ?? createTileMap(0, 0),
        spawn: options.spawn ? { x: options.spawn.x, y: options.spawn.y } : null,
        actors: {},
        combat: {},
        minds: {},
        dens,
        carcasses: {},
        deadfalls: {},
        corpses: {},
        signs: {},
        graves: {},
        villagers: {},
        chronicle: [],
        pressure: {},
        flora,
        inventories: {},
        lore: {},
        errands: {},
        weather: options.weather ? { ...options.weather } : createWeather(),
        lights: {},
        needs: {},
    };
}
/**
 * Bring a world loaded from an older build up to the current shape.
 *
 * World state gains tables as milestones land — `combat` in M2-01, `minds`,
 * `dens` and `carcasses` in M2-05 — and a snapshot written before one of them
 * existed comes back without it. The reader that parses those rows is
 * deliberately permissive (a row from a *newer* build must still load, or a
 * rollback becomes data loss), so the missing half arrives as `undefined` and
 * the first rule to touch it throws. That is exactly what happened to a world
 * saved before M2-01: `seatBody` assigned into `world.combat[id]` and there was
 * no `combat`.
 *
 * The filling is derived from `createWorld` rather than from a list, which is
 * the whole point: the next table added to a fresh world is carried into every
 * old save automatically, and there is no second place to forget to update.
 * Only *absent* keys are filled, so nothing a save actually recorded is
 * touched.
 *
 * Idempotent, and cheap enough to run on every load — it is a handful of key
 * comparisons against a world that was about to be cloned anyway.
 */
export function hydrateWorld(state) {
    const template = createWorld({ seed: state.seed });
    const hydrated = { ...state };
    for (const [key, value] of Object.entries(template)) {
        if (hydrated[key] === undefined)
            hydrated[key] = value;
    }
    // The same problem one level down: a save written between M2-01 and M2-05
    // has combatants, and none of them has the shield the fight now expects to
    // be able to break. A missing number here is an arithmetic bug rather than a
    // crash, which is worse — `undefined + 1` is `NaN`, and `NaN` in world state
    // is a replay that diverges silently.
    const combat = hydrated['combat'];
    if (combat) {
        for (const combatant of Object.values(combat))
            hydrateCombatant(combatant);
    }
    // The same, for minds saved before animals had anywhere to be going (M3-10).
    // A missing `wanderX` is worse than a crash: `chebyshev` of an `undefined`
    // is `NaN`, every comparison against it is false, and the animal would stand
    // there for ever looking exactly like the bug this ticket fixed.
    const minds = hydrated['minds'];
    if (minds) {
        for (const mind of Object.values(minds))
            hydrateMind(mind);
    }
    repairInsetDoors(hydrated['map']);
    repairVillageRebirthRelic(hydrated['map'], hydrated['spawn'] ?? null);
    return hydrated;
}
/**
 * Put the village relic back where its builders would have kept it: in open
 * land south of the settlement, never around the houses.
 *
 * Placed-site coordinates are not persisted, but the village spawn and its
 * well are. The well signature keeps this migration away from test arenas and
 * custom maps that merely happen to have a spawn. Saves which briefly shipped
 * with the village inside the ring have that mistaken work lifted first; older
 * saves simply gain the separate sanctuary.
 */
function repairVillageRebirthRelic(map, spawn) {
    if (!spawn)
        return;
    if (tileAt(map, spawn.x + 7, spawn.y - 2) !== 'well')
        return;
    const existing = sunStoneAt(map);
    if (existing && (existing.x !== spawn.x || existing.y !== spawn.y)) {
        const walking = walkDistancesFrom(map, spawn)[existing.y * map.width + existing.x];
        if (walking >= 0 && walking <= 70)
            return;
        // The first separated-circle migration judged nearness by coordinates and
        // could choose the far bank of the river without giving it an approach.
        // Preserve the holy site and wear a ritual way to it instead of moving the
        // stones a second time.
        carveSacredWay(map, spawn, existing);
        return;
    }
    // Undo the short-lived layout which put the settlement inside the stones.
    // Seven pillars replaced authored dirt; the southernmost replaced terrain
    // outside the stencil and is restored as unobtrusive open grass.
    if (existing) {
        setTile(map, spawn.x, spawn.y, 'dirt');
        for (const [dx, dy, kind] of [
            [0, -6, 'dirt'],
            [-8, -5, 'dirt'],
            [8, -5, 'dirt'],
            [-9, 0, 'dirt'],
            [9, 0, 'dirt'],
            [-8, 5, 'dirt'],
            [8, 5, 'dirt'],
            [0, 10, 'grass'],
        ]) {
            if (tileAt(map, spawn.x + dx, spawn.y + dy) === 'menhir') {
                setTile(map, spawn.x + dx, spawn.y + dy, kind);
            }
        }
    }
    const relic = southernRelicGround(map, spawn);
    if (!relic)
        return;
    for (let dy = -6; dy <= 6; dy += 1) {
        const halfWidth = RELIC_CLEARING_HALF_WIDTHS[dy + 6];
        for (let dx = -halfWidth; dx <= halfWidth; dx += 1) {
            setTile(map, relic.x + dx, relic.y + dy, 'grass');
        }
    }
    for (const [dx, dy] of RELIC_PILLARS) {
        setTile(map, relic.x + dx, relic.y + dy, 'menhir');
    }
    setTile(map, relic.x, relic.y, 'sun-stone');
    carveSacredWay(map, spawn, relic);
}
const RELIC_CLEARING_HALF_WIDTHS = [3, 5, 7, 8, 8, 8, 8, 8, 8, 8, 7, 5, 3];
const RELIC_PILLARS = [
    [0, -5],
    [-4, -4],
    [4, -4],
    [-7, 0],
    [7, 0],
    [-4, 4],
    [4, 4],
    [0, 5],
];
const RELIC_GROUND = [
    'grass',
    'dirt',
    'rock',
    'tree',
    'meadow',
    'ffridd',
    'oakwood',
    'hazelwood',
    'moor',
];
const RELIC_OPEN_GROUND = ['grass', 'dirt', 'meadow', 'ffridd', 'moor'];
function sunStoneAt(map) {
    for (let y = 0; y < map.height; y += 1) {
        for (let x = 0; x < map.width; x += 1) {
            if (tileAt(map, x, y) === 'sun-stone')
                return { x, y };
        }
    }
    return null;
}
/** Nearest sufficiently open clearing in the southern quarter around Llan-y-Nant. */
function southernRelicGround(map, spawn) {
    let best = null;
    const minY = Math.max(6, spawn.y + 20);
    const maxY = Math.min(map.height - 7, spawn.y + 70);
    const minX = Math.max(8, spawn.x - 60);
    const maxX = Math.min(map.width - 9, spawn.x + 60);
    for (let y = minY; y <= maxY; y += 1) {
        for (let x = minX; x <= maxX; x += 1) {
            let open = 0;
            let total = 0;
            let legal = true;
            for (let dy = -6; dy <= 6 && legal; dy += 1) {
                const halfWidth = RELIC_CLEARING_HALF_WIDTHS[dy + 6];
                for (let dx = -halfWidth; dx <= halfWidth; dx += 1) {
                    const kind = tileAt(map, x + dx, y + dy);
                    if (!RELIC_GROUND.includes(kind)) {
                        legal = false;
                        break;
                    }
                    total += 1;
                    if (RELIC_OPEN_GROUND.includes(kind))
                        open += 1;
                }
            }
            if (!legal || open / total < 0.55)
                continue;
            const score = Math.abs(y - (spawn.y + 28)) * 3 + Math.abs(x - spawn.x) + (1 - open / total) * 40;
            if (!best || score < best.score)
                best = { x, y, score };
        }
    }
    return best ? { x: best.x, y: best.y } : null;
}
const SACRED_WAY_BLOCKED = [
    'void',
    'sea',
    'crag',
    'wall',
    'floor',
    'door',
    'hearth',
    'well',
    'menhir',
    'cauldron',
    'mound',
    'wallow',
    'fence',
    'cot',
    'garden',
];
/**
 * Wear the short ritual way from the settlement to the older sanctuary.
 *
 * Unlike an ordinary path it may cross fresh water: those few cells become a
 * ford, keeping the river real while preventing a circle twenty-eight tiles
 * away on the map from becoming a hundred-and-fifty-tile resurrection walk.
 */
function carveSacredWay(map, from, to) {
    const cameFrom = new Int32Array(map.width * map.height).fill(-2);
    const start = from.y * map.width + from.x;
    const goal = to.y * map.width + to.x;
    const queue = [start];
    cameFrom[start] = -1;
    for (let head = 0; head < queue.length && cameFrom[goal] === -2; head += 1) {
        const index = queue[head];
        const x = index % map.width;
        const y = (index - x) / map.width;
        for (const [dx, dy] of [
            [0, 1],
            [-1, 1],
            [1, 1],
            [-1, 0],
            [1, 0],
            [0, -1],
            [-1, -1],
            [1, -1],
        ]) {
            const nx = x + dx;
            const ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= map.width || ny >= map.height)
                continue;
            const neighbour = ny * map.width + nx;
            if (cameFrom[neighbour] !== -2)
                continue;
            const kind = tileAt(map, nx, ny);
            if (neighbour !== goal && SACRED_WAY_BLOCKED.includes(kind))
                continue;
            cameFrom[neighbour] = index;
            queue.push(neighbour);
        }
    }
    if (cameFrom[goal] === -2)
        return;
    for (let cursor = goal; cursor >= 0; cursor = cameFrom[cursor]) {
        const x = cursor % map.width;
        const y = (cursor - x) / map.width;
        const kind = tileAt(map, x, y);
        if (kind === 'water')
            setTile(map, x, y, 'ford');
        else if (cursor !== goal &&
            cursor !== start &&
            kind !== 'ford' &&
            kind !== 'sun-stone' &&
            kind !== 'menhir') {
            setTile(map, x, y, 'path');
        }
    }
}
/** Eight-way walking distance, so a river on the map remains a real boundary. */
function walkDistancesFrom(map, start) {
    const distances = new Int32Array(map.width * map.height).fill(-1);
    const first = start.y * map.width + start.x;
    const queue = [first];
    distances[first] = 0;
    for (let head = 0; head < queue.length; head += 1) {
        const index = queue[head];
        const x = index % map.width;
        const y = (index - x) / map.width;
        for (const [dx, dy] of [
            [-1, -1],
            [0, -1],
            [1, -1],
            [-1, 0],
            [1, 0],
            [-1, 1],
            [0, 1],
            [1, 1],
        ]) {
            const nx = x + dx;
            const ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= map.width || ny >= map.height)
                continue;
            const neighbour = ny * map.width + nx;
            if (distances[neighbour] >= 0 || !isPassable(map, nx, ny))
                continue;
            distances[neighbour] = distances[index] + 1;
            queue.push(neighbour);
        }
    }
    return distances;
}
/**
 * Repair the three small village houses shipped with their `+` one tile inside
 * an unbroken side wall (`#__+#` / `#+_c#`). This is deliberately structural
 * rather than coordinate-based: saves do not retain placed-site coordinates,
 * and the same authored village can sit anywhere in a generated valley.
 *
 * Only horizontal inset doors are touched. Real doors already in an exterior
 * wall have open ground on one side, so the operation is idempotent and leaves
 * the hall, bothies, gates and any later interior doorway alone.
 */
function repairInsetDoors(map) {
    const interior = (x, y) => {
        const kind = tileAt(map, x, y);
        return kind === 'floor' || kind === 'cot';
    };
    for (let y = 0; y < map.height; y += 1) {
        for (let x = 0; x < map.width; x += 1) {
            if (tileAt(map, x, y) !== 'door')
                continue;
            if (tileAt(map, x - 1, y) === 'wall' && interior(x + 1, y)) {
                setTile(map, x - 1, y, 'door');
                setTile(map, x, y, 'floor');
            }
            else if (tileAt(map, x + 1, y) === 'wall' && interior(x - 1, y)) {
                setTile(map, x + 1, y, 'door');
                setTile(map, x, y, 'floor');
            }
        }
    }
}
/**
 * Fields a `Combatant` has gained since the first save format, with the value a
 * fresh one starts at. Declared beside the migration rather than beside
 * `createCombatant` because this list is *only* about what old rows can lack —
 * it is a record of history, and it should not grow every time a field does.
 */
const COMBATANT_BACKFILL = {
    // M4-05 and M4-06: a body saved before the surgery and the rescue has
    // neither a standing request for its leg nor anybody holding it.
    consent: null,
    burden: null,
    shieldWear: 0,
    band: 'fresh',
    guarding: false,
    swing: null,
    recoveryUntil: 0,
    queued: null,
    queuedAt: 0,
    riposteAgainst: null,
    riposteUntil: 0,
    proneUntil: 0,
    channel: null,
};
/**
 * Where a mind saved before M3-10 is told to go: home.
 *
 * `homeX`/`homeY` are as old as the table itself, so the wander target can be
 * filled from the record rather than from a default — which means a restored
 * animal starts by standing where it already is and picking somewhere to go, and
 * behaves identically to one that was born this tick.
 */
function hydrateMind(mind) {
    const record = mind;
    if (record['wanderX'] === undefined)
        record['wanderX'] = mind.homeX;
    if (record['wanderY'] === undefined)
        record['wanderY'] = mind.homeY;
    if (record['pauseUntil'] === undefined)
        record['pauseUntil'] = 0;
}
function hydrateCombatant(combatant) {
    const record = combatant;
    for (const [key, value] of Object.entries(COMBATANT_BACKFILL)) {
        if (record[key] === undefined)
            record[key] = value;
    }
    // A fresh array per combatant: a shared one would give every fighter in the
    // world the same defence history the moment one of them defended.
    if (!Array.isArray(record['defences']))
        record['defences'] = [];
}
/**
 * Nesting depth past which we assume a cycle rather than a deep world.
 * Plain data has no cycles; something has gone wrong if we get here.
 */
const MAX_DEPTH = 64;
/**
 * Copy world state so that mutating the copy cannot touch the original.
 *
 * Structural rather than field-by-field on purpose: a hand-written clone that
 * forgets a field added three milestones later shares that field's reference
 * between the caller's state and the returned one, and the symptom is a replay
 * that diverges only on the second run — hours to find, one line to cause.
 *
 * Non-plain values throw rather than being copied by reference. World state
 * has to survive JSON for persistence (M0-07), the wire (M0-04) and hashing,
 * so a `Map` or a class instance is already a bug; this is the earliest and
 * loudest place to say so.
 */
export function cloneWorld(state) {
    return deepClone(state, '$', 0);
}
function deepClone(value, path, depth) {
    if (value === null || typeof value !== 'object') {
        if (typeof value === 'function' || typeof value === 'symbol' || typeof value === 'bigint') {
            throw new TypeError(`${typeof value} at ${path} cannot live in world state`);
        }
        return value;
    }
    if (depth > MAX_DEPTH) {
        throw new TypeError(`world state nests deeper than ${MAX_DEPTH} at ${path} — a cycle?`);
    }
    if (Array.isArray(value)) {
        // Fast path for arrays of primitives — which, since M0-04, means the tile
        // map: 16,384 numbers cloned ten times a second. The general path below
        // builds a `$.map.tiles[9001]` string per element purely so that a throw
        // can name the culprit, and those allocations dominate the tick. Scan
        // once, and only fall back when there is something to recurse into.
        let plainValues = true;
        for (let i = 0; i < value.length; i += 1) {
            const item = value[i];
            if (item !== null && typeof item === 'object') {
                plainValues = false;
                break;
            }
            if (typeof item === 'function' || typeof item === 'symbol' || typeof item === 'bigint') {
                throw new TypeError(`${typeof item} at ${path}[${i}] cannot live in world state`);
            }
        }
        if (plainValues)
            return value.slice();
        return value.map((item, i) => deepClone(item, `${path}[${i}]`, depth + 1));
    }
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) {
        throw new TypeError(`non-plain object at ${path}: world state must be plain data`);
    }
    const copy = {};
    for (const [key, member] of Object.entries(value)) {
        copy[key] = deepClone(member, `${path}.${key}`, depth + 1);
    }
    return copy;
}
//# sourceMappingURL=world.js.map